import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import * as Icons from 'lucide-react';
import { Link } from 'react-router-dom';
import { servicesData } from '../data/mockDataExt';
import { useSEO } from '../hooks/useSEO';

const Services = () => {
    const { t } = useTranslation();

    const IconComponent = ({ name, size }: { name: string; size: number }) => {
        const LucideIcon = Icons[name as keyof typeof Icons] as React.ElementType;
        return LucideIcon ? <LucideIcon size={size} /> : null;
    };

    useSEO({
        title: 'Servicios de Logística y Reciclaje | Laz Metals América',
        description: 'Capacidad operativa masiva: recolección con flota especializada, compra/venta corporativa de metales y suministro directo y clasificado a fundiciones.',
    });

    return (
        <div className="w-full">
            {/* HEADER SECTION */}
            <section className="min-h-[80dvh] lg:min-h-[60vh] py-16 lg:py-32 massive-container flex flex-col items-center justify-center relative overflow-hidden mt-6 group">
                <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-[15s] group-hover:scale-105"
                    style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1519003722824-194d4455a60c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80)' }}
                />
                <div className="absolute inset-0 bg-bg-tertiary/90 backdrop-blur-[1px]" />

                <div className="container mx-auto px-4 md:px-12 max-w-5xl text-left md:text-center relative z-10 text-white">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="relative z-10 px-4 md:px-12 max-w-4xl"
                    >
                        <div className="section-tag-orange mb-6 text-primary-light border-primary/30 md:mx-auto inline-flex">{t('servicesPage.tag')}</div>
                        <h1
                            className="text-3xl md:text-5xl lg:text-7xl font-heading font-medium mb-8 leading-tight text-white drop-shadow-md"
                            dangerouslySetInnerHTML={{ __html: t('servicesPage.title') }}
                        />
                        <p className="text-gray-300 font-body text-lg md:text-xl font-light leading-relaxed mb-10">
                            {t('servicesPage.desc')}
                        </p>
                        <Link to="/contacto" className="btn-primary inline-flex items-center group">
                            {t('servicesPage.ctaBook')}
                            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* SERVICES GRID */}
            <section className="py-24">
                <div className="container mx-auto px-6 max-w-7xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        {servicesData.map((service, idx) => {
                            return (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-100px" }}
                                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                                    className="bg-white rounded-2xl p-10 md:p-12 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow duration-300"
                                >
                                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-8">
                                        <IconComponent name={service.icon} size={32} />
                                    </div>
                                    <h2 className="text-2xl md:text-3xl font-heading font-medium text-accent mb-6">
                                        {t(service.title)}
                                    </h2>
                                    <p className="text-gray-600 font-body text-base lg:text-lg leading-relaxed mb-8">
                                        {t(service.description)}
                                    </p>
                                    <ul className="space-y-4">
                                        {service.bulletPoints.map((point, i) => (
                                            <li key={i} className="flex items-start">
                                                <CheckCircle2 className="w-6 h-6 text-secondary shrink-0 mt-0.5 mr-4" />
                                                <span className="text-gray-700 font-body leading-relaxed">{t(point)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FINAL CTA Massive Block */}
            <section className="pb-24">
                <div className="w-[95%] mx-auto bg-bg-secondary rounded-block p-16 text-center shadow-subtle relative overflow-hidden group">
                    <div
                        className="absolute inset-0 bg-cover bg-center transition-transform duration-[15s] group-hover:scale-105"
                        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1586864387789-628af9feed72?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80)' }}
                    />
                    <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="max-w-4xl mx-auto relative z-10"
                    >
                        <h2 className="text-3xl md:text-5xl font-heading font-medium mb-6 text-white text-shadow-sm">
                            {t('servicesPage.finalTitle')}
                        </h2>
                        <p className="text-xl text-gray-200 font-body mb-10 max-w-2xl mx-auto font-light">
                            {t('servicesPage.finalDesc')}
                        </p>
                        <Link to="/contacto" className="btn-secondary inline-flex items-center group shadow-lg hover:shadow-xl transition-shadow text-lg px-8 py-4">
                            {t('servicesPage.finalCta')}
                            <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </motion.div>
                </div>
            </section>

        </div>
    );
};

export default Services;
